import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { getSpese, createSpesa, updateSpesa, deleteSpesa, getFornitori, getPersonale, uploadScontrinoSpesa } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Receipt, User, Truck, X, Lock, ChevronDown, ChevronRight, Calendar, Eye, Pencil, Upload, Image } from 'lucide-react';
import { format, subDays, startOfMonth, isWithinInterval, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const CATEGORIE = [
  { value: 'personale', label: 'Personale', color: 'text-blue-500', bg: 'bg-blue-500/10', icon: User },
  { value: 'varie', label: 'Varie', color: 'text-purple-500', bg: 'bg-purple-500/10', icon: null },
  { value: 'fornitori', label: 'Fornitori', color: 'text-green-500', bg: 'bg-green-500/10', icon: Truck },
  { value: 'generiche', label: 'Generiche', color: 'text-orange-500', bg: 'bg-orange-500/10', icon: null },
];

const PERIODI = [
  { value: 'oggi', label: 'Oggi' },
  { value: '7giorni', label: 'Ultimi 7 giorni' },
  { value: 'mese', label: 'Questo mese' },
  { value: 'tutto', label: 'Tutte' },
  { value: 'personalizzato', label: 'Personalizzato' },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Costruisce l'URL dello scontrino aggiungendo il token JWT come query param
// (necessario per le immagini servite da cloud storage che richiedono auth)
const buildScontrinoUrl = (url) => {
  if (!url) return '';
  const fullUrl = `${API_BASE}${url}`;
  if (url.startsWith('/api/files/')) {
    const token = localStorage.getItem('token');
    return token ? `${fullUrl}?auth=${encodeURIComponent(token)}` : fullUrl;
  }
  return fullUrl;
};

const Spese = () => {
  const [spese, setSpese] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [personale, setPersonale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterPeriodo, setFilterPeriodo] = useState('7giorni');
  const [dataInizio, setDataInizio] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dataFine, setDataFine] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expandedDays, setExpandedDays] = useState({});
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Edit/View mode
  const [editMode, setEditMode] = useState(null); // null | 'create' | 'edit'
  const [selectedSpesa, setSelectedSpesa] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewSpesa, setViewSpesa] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    importo: '',
    categoria: '',
    data: format(new Date(), 'yyyy-MM-dd'),
    data_lavorativa: '',  // Giornata lavorativa (per chiusure dopo mezzanotte)
    persona: '',
    fornitore_nome: '',
    note: ''
  });
  const [formFile, setFormFile] = useState(null);
  const [formFilePreview, setFormFilePreview] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-expand today
  useEffect(() => {
    const oggi = format(new Date(), 'yyyy-MM-dd');
    setExpandedDays(prev => ({ ...prev, [oggi]: true }));
  }, []);

  const loadData = async () => {
    try {
      const [speseData, fornitoriData, personaleData] = await Promise.all([
        getSpese(),
        getFornitori(),
        getPersonale()
      ]);
      setSpese(speseData);
      setFornitori(fornitoriData);
      setPersonale(personaleData);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // Calcola la data lavorativa di default
    // Se è tra 00:00 e 06:00, suggerisci il giorno precedente
    const now = new Date();
    const hour = now.getHours();
    const oggi = format(now, 'yyyy-MM-dd');
    let dataLavorativaDefault = '';
    
    if (hour >= 0 && hour < 6) {
      // Dopo mezzanotte, prima delle 6 → suggerisci giorno precedente
      const ieri = new Date(now);
      ieri.setDate(ieri.getDate() - 1);
      dataLavorativaDefault = format(ieri, 'yyyy-MM-dd');
    }
    
    setFormData({
      importo: '',
      categoria: '',
      data: oggi,
      data_lavorativa: dataLavorativaDefault,
      persona: '',
      fornitore_nome: '',
      note: ''
    });
    setFormFile(null);
    setFormFilePreview(null);
    setEditMode(null);
    setSelectedSpesa(null);
  };

  const handleFormFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFormFile = () => {
    setFormFile(null);
    setFormFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.importo || !formData.categoria) {
      toast.error('Importo e categoria sono obbligatori');
      return;
    }
    
    if (formData.categoria === 'personale' && !formData.persona) {
      toast.error('Seleziona chi ha ricevuto il pagamento');
      return;
    }
    if (formData.categoria === 'fornitori' && !formData.fornitore_nome) {
      toast.error('Seleziona il fornitore');
      return;
    }
    
    let descrizione = formData.categoria.charAt(0).toUpperCase() + formData.categoria.slice(1);
    if (formData.categoria === 'personale' && formData.persona) {
      descrizione = `Pagamento ${formData.persona}`;
    } else if (formData.categoria === 'fornitori' && formData.fornitore_nome) {
      descrizione = `Pagamento ${formData.fornitore_nome}`;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        descrizione,
        importo: parseFloat(formData.importo),
        persona: formData.categoria === 'personale' ? formData.persona : null,
        fornitore_nome: formData.categoria === 'fornitori' ? formData.fornitore_nome : null,
      };

      let createdId = null;
      if (editMode === 'edit' && selectedSpesa) {
        await updateSpesa(selectedSpesa.id, payload);
        toast.success('Spesa aggiornata!');
      } else {
        const result = await createSpesa(payload);
        createdId = result.id;
        toast.success('Spesa registrata!');
        // Espandi il giorno della nuova spesa
        setExpandedDays(prev => ({ ...prev, [formData.data]: true }));
      }
      
      // Upload foto se presente (solo per nuova creazione)
      if (formFile && createdId) {
        try {
          await uploadScontrinoSpesa(createdId, formFile);
          toast.success('Scontrino caricato!');
        } catch (uploadError) {
          toast.error('Spesa salvata, ma errore nel caricamento scontrino');
        }
      }
      
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (spesa) => {
    setSelectedSpesa(spesa);
    setEditMode('edit');
    setFormData({
      importo: spesa.importo?.toString() || '',
      categoria: spesa.categoria || '',
      data: spesa.data || format(new Date(), 'yyyy-MM-dd'),
      data_lavorativa: spesa.data_lavorativa || spesa.data || '',
      persona: spesa.persona || '',
      fornitore_nome: spesa.fornitore_nome || '',
      note: spesa.note || ''
    });
    setShowForm(true);
  };

  const handleView = (spesa) => {
    setViewSpesa(spesa);
    setViewDialog(true);
  };

  const handleDelete = async (id, spesa) => {
    if (!isAdmin && !spesa.is_owner) {
      toast.error('Non puoi eliminare le spese di altri');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questa spesa?')) return;
    try {
      await deleteSpesa(id);
      toast.success('Spesa eliminata!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUploadScontrino = async (spesaId, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadScontrinoSpesa(spesaId, file);
      toast.success('Scontrino caricato!');
      loadData();
      // Aggiorna anche la vista se aperta
      if (viewSpesa && viewSpesa.id === spesaId) {
        setViewSpesa({ ...viewSpesa, scontrino_url: result.scontrino_url });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  // Filtra per periodo
  const filteredByPeriod = useMemo(() => {
    const oggi = new Date();
    oggi.setHours(23, 59, 59, 999);
    
    return spese.filter(s => {
      const dataSpesa = parseISO(s.data);
      
      switch (filterPeriodo) {
        case 'oggi':
          return s.data === format(new Date(), 'yyyy-MM-dd');
        case '7giorni':
          return isWithinInterval(dataSpesa, { start: subDays(oggi, 7), end: oggi });
        case 'mese':
          return isWithinInterval(dataSpesa, { start: startOfMonth(oggi), end: oggi });
        case 'personalizzato':
          return isWithinInterval(dataSpesa, { 
            start: parseISO(dataInizio), 
            end: parseISO(dataFine) 
          });
        case 'tutto':
        default:
          return true;
      }
    });
  }, [spese, filterPeriodo, dataInizio, dataFine]);

  // Filtra per categoria
  const filteredSpese = useMemo(() => {
    return filterCategoria === 'all'
      ? filteredByPeriod
      : filteredByPeriod.filter(s => s.categoria === filterCategoria);
  }, [filteredByPeriod, filterCategoria]);

  // Raggruppa per giorno
  const spesePerGiorno = useMemo(() => {
    const grouped = {};
    filteredSpese.forEach(spesa => {
      if (!grouped[spesa.data]) {
        grouped[spesa.data] = [];
      }
      grouped[spesa.data].push(spesa);
    });
    // Ordina per data decrescente
    return Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([data, spese]) => ({
        data,
        spese,
        totale: spese.reduce((acc, s) => acc + s.importo, 0),
        categorie: [...new Set(spese.map(s => s.categoria))]
      }));
  }, [filteredSpese]);

  const totaleSpese = filteredSpese.reduce((acc, s) => acc + s.importo, 0);

  const getCategoriaInfo = (cat) => {
    return CATEGORIE.find(c => c.value === cat) || { label: cat, color: 'text-muted-foreground', bg: 'bg-secondary' };
  };

  const getSpesaDetail = (spesa) => {
    if (spesa.categoria === 'personale' && spesa.persona) {
      return spesa.persona;
    }
    if (spesa.categoria === 'fornitori' && spesa.fornitore_nome) {
      return spesa.fornitore_nome;
    }
    return spesa.descrizione;
  };

  const toggleDay = (data) => {
    setExpandedDays(prev => ({ ...prev, [data]: !prev[data] }));
  };

  const formatDayLabel = (dataStr) => {
    const oggi = format(new Date(), 'yyyy-MM-dd');
    const ieri = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    
    if (dataStr === oggi) return 'Oggi';
    if (dataStr === ieri) return 'Ieri';
    return format(parseISO(dataStr), 'EEEE d MMMM', { locale: it });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="spese-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Spese</h1>
          <p className="text-muted-foreground mt-1">Registra e monitora le spese</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setEditMode('create'); setShowForm(true); }} data-testid="add-spesa-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuova Spesa
          </Button>
        )}
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterPeriodo} onValueChange={setFilterPeriodo}>
          <SelectTrigger className="w-[160px]" data-testid="filter-periodo">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Periodo" />
          </SelectTrigger>
          <SelectContent>
            {PERIODI.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filterPeriodo === 'personalizzato' && (
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={dataInizio}
              onChange={(e) => setDataInizio(e.target.value)}
              className="w-[140px]"
            />
            <span className="text-muted-foreground">→</span>
            <Input
              type="date"
              value={dataFine}
              onChange={(e) => setDataFine(e.target.value)}
              className="w-[140px]"
            />
          </div>
        )}

        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-[160px]" data-testid="filter-categoria">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            {CATEGORIE.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Form Inline */}
      {showForm && (
        <Card className="border-primary/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {editMode === 'edit' ? 'Modifica Spesa' : 'Registra Nuova Spesa'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data">Data Registrazione</Label>
                  <Input
                    id="data"
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    className="h-12"
                    data-testid="spesa-data"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_lavorativa" className="flex items-center gap-2">
                    Giornata Lavorativa
                    <span className="text-xs text-muted-foreground font-normal">(per chiusura cassa)</span>
                  </Label>
                  <Input
                    id="data_lavorativa"
                    type="date"
                    value={formData.data_lavorativa || formData.data}
                    onChange={(e) => setFormData({ ...formData, data_lavorativa: e.target.value })}
                    className="h-12"
                    data-testid="spesa-data-lavorativa"
                  />
                  {formData.data_lavorativa && formData.data_lavorativa !== formData.data && (
                    <p className="text-xs text-yellow-500">
                      Questa spesa verrà conteggiata nella chiusura del {format(parseISO(formData.data_lavorativa), 'd MMMM', { locale: it })}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value, persona: '', fornitore_nome: '' })}
                >
                  <SelectTrigger className="h-12" data-testid="spesa-categoria">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIE.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.categoria === 'personale' && (
                <div className="space-y-2">
                  <Label>Chi? *</Label>
                  {personale.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3 bg-secondary/50 rounded">
                      Nessun personale configurato. Vai su Personale per aggiungerne.
                    </p>
                  ) : (
                    <Select
                      value={formData.persona}
                      onValueChange={(value) => setFormData({ ...formData, persona: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="spesa-persona">
                        <SelectValue placeholder="Seleziona persona" />
                      </SelectTrigger>
                      <SelectContent>
                        {personale.map((p) => (
                          <SelectItem key={p.id} value={p.nome}>
                            {p.nome} {p.ruolo && `(${p.ruolo})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {formData.categoria === 'fornitori' && (
                <div className="space-y-2">
                  <Label>Quale fornitore? *</Label>
                  {fornitori.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3 bg-secondary/50 rounded">
                      Nessun fornitore configurato. Vai su Fornitori per aggiungerne.
                    </p>
                  ) : (
                    <Select
                      value={formData.fornitore_nome}
                      onValueChange={(value) => setFormData({ ...formData, fornitore_nome: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="spesa-fornitore">
                        <SelectValue placeholder="Seleziona fornitore" />
                      </SelectTrigger>
                      <SelectContent>
                        {fornitori.map((f) => (
                          <SelectItem key={f.id} value={f.nome}>
                            {f.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="importo">Importo € *</Label>
                <Input
                  id="importo"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={formData.importo}
                  onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                  placeholder="0.00"
                  className="h-12 text-lg"
                  data-testid="spesa-importo"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="note">Note (opzionale)</Label>
                <Input
                  id="note"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="h-12"
                  data-testid="spesa-note"
                />
              </div>

              {/* Upload foto scontrino - solo in creazione */}
              {editMode === 'create' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Foto Scontrino (opzionale)
                  </Label>
                  
                  {formFilePreview ? (
                    <div className="relative">
                      <img 
                        src={formFilePreview}
                        alt="Anteprima scontrino"
                        className="w-full max-h-48 object-contain rounded-lg border bg-secondary/30"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={removeFormFile}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFormFileChange}
                        data-testid="spesa-file-input"
                      />
                      <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Clicca per scattare o selezionare foto
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" disabled={saving} data-testid="spesa-submit">
                  {saving ? 'Salvataggio...' : editMode === 'edit' ? 'Aggiorna' : 'Registra Spesa'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Totale */}
      <Card className="bg-gradient-to-br from-destructive/10 to-transparent border-destructive/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                Totale {filterCategoria !== 'all' ? getCategoriaInfo(filterCategoria).label : 'Spese'}
                <span className="ml-2 text-xs">
                  ({filteredSpese.length} {filteredSpese.length === 1 ? 'spesa' : 'spese'})
                </span>
              </p>
              <p className="text-3xl font-bold font-mono text-destructive">
                €{totaleSpese.toFixed(2)}
              </p>
            </div>
            <Receipt className="h-10 w-10 text-destructive/50" />
          </div>
        </CardContent>
      </Card>

      {/* Lista spese raggruppate per giorno */}
      {spesePerGiorno.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessuna spesa nel periodo selezionato</p>
            <p className="text-sm text-muted-foreground">Clicca su "Nuova Spesa" per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {spesePerGiorno.map(({ data, spese: speseGiorno, totale, categorie }) => (
            <Collapsible
              key={data}
              open={expandedDays[data]}
              onOpenChange={() => toggleDay(data)}
            >
              <Card className="overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 items-start">
                      <div className="flex items-center gap-2">
                        {expandedDays[data] ? (
                          <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        )}
                        <span className="font-semibold capitalize text-sm sm:text-base">
                          {formatDayLabel(data)}
                        </span>
                        <span className="text-xs text-muted-foreground sm:hidden">
                          ({speseGiorno.length})
                        </span>
                      </div>
                      <div className="flex gap-1 flex-wrap ml-6 sm:ml-0">
                        {categorie.map(cat => {
                          const info = getCategoriaInfo(cat);
                          return (
                            <span 
                              key={cat} 
                              className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${info.bg} ${info.color}`}
                            >
                              {info.label}
                            </span>
                          );
                        })}
                      </div>
                      <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline">
                        {speseGiorno.length} {speseGiorno.length === 1 ? 'spesa' : 'spese'}
                      </span>
                    </div>
                    <span className="font-bold font-mono text-base sm:text-lg text-destructive whitespace-nowrap">
                      €{totale.toFixed(2)}
                    </span>
                  </button>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t divide-y">
                    {speseGiorno.map((spesa) => {
                      const catInfo = getCategoriaInfo(spesa.categoria);
                      const canEdit = isAdmin || spesa.is_owner;
                      return (
                        <div 
                          key={spesa.id} 
                          className="p-3 sm:p-4 hover:bg-secondary/20"
                          data-testid={`spesa-row-${spesa.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 sm:gap-4 flex-1 min-w-0">
                              <div className={`w-1 h-full min-h-[40px] rounded-full flex-shrink-0 ${catInfo.color.replace('text-', 'bg-')}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                  <span className="font-medium text-sm sm:text-base truncate">{getSpesaDetail(spesa)}</span>
                                  <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${catInfo.bg} ${catInfo.color} flex-shrink-0`}>
                                    {catInfo.label}
                                  </span>
                                  {spesa.scontrino_url && (
                                    <Image className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                                {spesa.note && (
                                  <p className="text-xs sm:text-sm text-muted-foreground truncate">{spesa.note}</p>
                                )}
                                {isAdmin && spesa.user_nome && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Inserita da: {spesa.user_nome}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2 flex-shrink-0">
                              <span className="font-mono font-bold text-sm sm:text-base whitespace-nowrap">€{spesa.importo.toFixed(2)}</span>
                              <div className="flex items-center gap-0.5 sm:gap-1">
                                {/* View button */}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 sm:h-8 sm:w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleView(spesa);
                                  }}
                                  title="Visualizza"
                                  data-testid={`view-spesa-${spesa.id}`}
                                >
                                  <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                </Button>
                                
                                {/* Edit button */}
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 sm:h-8 sm:w-8"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(spesa);
                                    }}
                                    title="Modifica"
                                    data-testid={`edit-spesa-${spesa.id}`}
                                  >
                                    <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                  </Button>
                                )}
                                
                                {/* Delete button */}
                                {canEdit ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive h-7 w-7 sm:h-8 sm:w-8"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(spesa.id, spesa);
                                    }}
                                    data-testid={`delete-spesa-${spesa.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                  </Button>
                                ) : (
                                  <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Dettaglio Spesa - {viewSpesa && format(parseISO(viewSpesa.data), 'dd/MM/yyyy')}
            </DialogTitle>
            <DialogDescription>
              Visualizza i dettagli della spesa e carica lo scontrino
            </DialogDescription>
          </DialogHeader>
          
          {viewSpesa && (
            <div className="space-y-4">
              {/* Dettagli */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Importo</p>
                  <p className="text-xl font-bold font-mono text-destructive">€{viewSpesa.importo.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Categoria</p>
                  <p className={`text-lg font-semibold ${getCategoriaInfo(viewSpesa.categoria).color}`}>
                    {getCategoriaInfo(viewSpesa.categoria).label}
                  </p>
                </div>
              </div>

              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Descrizione</p>
                <p className="font-medium">{getSpesaDetail(viewSpesa)}</p>
              </div>

              {viewSpesa.note && (
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Note</p>
                  <p className="text-sm">{viewSpesa.note}</p>
                </div>
              )}

              {viewSpesa.user_nome && (
                <div className="text-sm text-muted-foreground">
                  Inserita da: <span className="font-medium">{viewSpesa.user_nome}</span>
                </div>
              )}

              {/* Scontrino */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Foto Scontrino
                </Label>
                
                {viewSpesa.scontrino_url ? (
                  <div className="relative">
                    <img 
                      src={buildScontrinoUrl(viewSpesa.scontrino_url)}
                      alt="Scontrino spesa"
                      className="w-full rounded-lg border"
                    />
                    <a 
                      href={buildScontrinoUrl(viewSpesa.scontrino_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                    <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Nessuno scontrino caricato</p>
                    
                    {(isAdmin || viewSpesa.is_owner) && (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleUploadScontrino(viewSpesa.id, e.target.files[0]);
                            }
                          }}
                          disabled={uploading}
                        />
                        <Button variant="outline" size="sm" disabled={uploading} asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading ? 'Caricamento...' : 'Carica Scontrino'}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                )}

                {/* Upload button if already has image */}
                {viewSpesa.scontrino_url && (isAdmin || viewSpesa.is_owner) && (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleUploadScontrino(viewSpesa.id, e.target.files[0]);
                        }
                      }}
                      disabled={uploading}
                    />
                    <Button variant="outline" size="sm" disabled={uploading} className="w-full" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Caricamento...' : 'Sostituisci Scontrino'}
                      </span>
                    </Button>
                  </label>
                )}
              </div>

              {/* Edit button in dialog */}
              {(isAdmin || viewSpesa.is_owner) && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    setViewDialog(false);
                    handleEdit(viewSpesa);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Modifica Spesa
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Spese;
